import {
  Divider,
  Typography,
  makeStyles,
  AlertDialog,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Hidden,
  IconButton,
  Scaler,
  Tab,
  Tabs,
  useIsMobile,
  useBreakpoint
} from '@paintscout/ui'
import type { MuiTheme as Theme, DialogProps } from '@paintscout/ui'
import {
  Theme as ThemeOverride,
  DialogStackContext,
  DropdownSelect,
  useClientOptions,
  useUser
} from '@paintscout/ui-business'
import type { EditorMethods, DropdownSelectOption } from '@paintscout/ui-business'
import ErrorIcon from '@material-ui/icons/Error'
import FullscreenIcon from '@material-ui/icons/Fullscreen'
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit'
import SendIcon from '@material-ui/icons/Send'
import type { SentEmail, User } from '@paintscout/api'
import {
  getCustomEmailTemplates,
  getEmailTemplates,
  getFeature,
  getFollowUps,
  getObjectLabels,
  getQuotePresentation,
  hasIntegrationInterface,
  reformatSpecialChars,
  toggleFollowUp
} from '@paintscout/util/builder'
import { combineContexts, createQuoteContext, createUserContext, template } from '@paintscout/util/templater'
import type { QuoteProps } from '@paintscout/react-quote'
import {
  Presentation,
  PresentationNavContextProvider,
  PresentationNavMobile,
  QuoteContextProvider,
  useQuote
} from '@paintscout/react-quote'
import { useQuoteMutations } from 'shared/hooks'
import { track } from 'shared/util/analytics'
import striptags from 'striptags'

import { isEmailValid, isPhoneNumberValid } from '@paintscout/util/util'
import * as Sentry from '@sentry/react'
import { Formik } from 'formik'
import isEqual from 'lodash/isEqual'
import { useSnackbar } from 'notistack'
import type { EmailTemplate, OptionsDocument, PresentationEstimator, QuoteDocument } from 'paintscout'
import React, { useContext, useMemo, useRef, useState } from 'react'
import SplitterLayout from 'react-splitter-layout'
import * as Yup from 'yup'
import type { SendQuoteFormValues } from './SendQuoteForm'
import SendQuoteForm from './SendQuoteForm'

const WORK_ORDER_VIEW = 'work-order'

export interface SendQuoteDialogProps extends DialogProps {
  loading?: boolean
  view: 'quote' | 'work-order' | 'presentation'
  onConfirm?: () => void
  onCancel?: () => void
}

export const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    '&:not($paperFullScreen)': {
      height: '80vh',
      maxWidth: '1200px'
    }
  },
  paperFullScreen: {},
  dialogContent: {
    height: '100%',
    paddingTop: 0,
    paddingBottom: 0
  },
  dialogActions: {
    '& > div:first-of-type': {
      // Remove the negative margin so Cancel button aligns with Send button on the right
      marginLeft: '0 !important'
    }
  },
  splitPaneWrapper: {
    position: 'relative',
    height: '100%',
    width: '100%'
  },
  splitterLayout: {
    '& .layout-splitter': {
      background: '#DDDDDD',
      opacity: 0.5,
      zIndex: 1,
      backgroundClip: 'padding-box',
      width: '12px',
      margin: '0 -5px',
      borderLeft: '5px solid rgba(255, 255, 255, 0)',
      borderRight: '5px solid rgba(255, 255, 255, 0)',
      cursor: 'col-resize'
    },
    '& .layout-pane:not(.layout-pane-primary)': {
      // fix scrollbar flickering on Scaler when there's a small amount of scrolling
      overflow: 'scroll'
    }
  },
  quotePaper: {
    boxShadow: 'none'
  },
  form: {
    paddingRight: theme.spacing(),
    marginRight: theme.spacing()
  },
  secondaryAction: {
    height: theme.typography.pxToRem(50),
    borderTop: '1px #DDDDDD solid',
    margin: 0,
    paddingLeft: theme.typography.pxToRem(0),
    paddingRight: theme.typography.pxToRem(0),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  tabs: {
    marginLeft: -8,
    marginRight: -8
  },

  presentationWrapper: {
    paddingTop: theme.paintscout.header.height.md,
    pointerEvents: 'none'
  },
  previewToolbar: {
    width: '100%'
  },
  previewToolbarRoot: {
    width: '95.9%',
    marginRight: theme.spacing(2.5)
  }
}))

interface Email {
  name: 'To Client' | 'To Staff'
  silentQuoteLink?: boolean
  signature?: string
  values: SendQuoteFormValues
}

function getDefaultEmail({
  user,
  autofillTo,
  quote,
  options,
  emailTemplate
}: {
  user: User
  autofillTo?: boolean
  quote: QuoteDocument
  options: OptionsDocument
  emailTemplate?: EmailTemplate
}): SendQuoteFormValues {
  const { contact, secondaryContact } = quote
  let from = {
    name: '',
    email: ''
  }
  if (options.options.quotes.quoteEmailFromEstimator && quote.owner?.email && quote.owner?.firstName) {
    from = {
      name: `${quote.owner.firstName}${quote.owner.lastName ? ' ' + quote.owner.lastName : ''}`,
      email: quote.owner.email
    }
  } else if (user.name && user.email) {
    from = {
      name: user.name,
      email: user.email
    }
  } else if (user.email) {
    from = {
      name: '',
      email: user.email
    }
  }

  const templateContext = combineContexts(
    createQuoteContext({ quote, options }),
    createUserContext({ user: { ...user, ...user.user_metadata } })
  )

  const to = [
    ...(autofillTo ? [contact?.email, contact?.alternate_email] : []),
    ...(autofillTo ? [secondaryContact?.email, secondaryContact?.alternate_email] : []),
    ...(emailTemplate.additionalRecipients ?? [])
  ].filter((x) => !!x)

  /* 
    This is a bit ugly - we don't really want an HTML editor for the subject, 
    but we do need to have template tags in it... So the profile where it's edited
    is an HTML editor, but then we wind-up having to strip/convert it back to text.
    That might be unavoidable/easiest.
  */
  const subject = reformatSpecialChars(striptags(template({ context: templateContext, text: emailTemplate.subject })))

  /* 
    Slightly different methodology here - strip all non numbers, and then we'll
    just try to validate that there's an appropriate number of digits
   */
  const phoneNumber = (contact?.phone_number ?? '').replace('+1', '').replace(/[^0-9.]*/g, '')

  return {
    to,
    from,
    subject,
    body: template({ context: templateContext, text: emailTemplate.body }),
    sms: {
      enabled: false,
      to: (autofillTo && phoneNumber) || ''
    },
    attachPdf: emailTemplate.attachPdf,
    signature: emailTemplate.signature
  }
}

const ValidationSchema = Yup.object().shape({
  to: Yup.array().required('Required'),
  sms: Yup.object({
    to: Yup.string().when('enabled', {
      is: true,
      then: Yup.string()
        .test('valid-phone', 'Please enter a valid phone number', (value) => isPhoneNumberValid(value))
        .required(`Please enter a phone number`)
    })
  }),
  body: Yup.string().required('Required'),
  subject: Yup.string().required('Required')
})

export default function SendQuoteDialog({ view, onConfirm, ...props }: SendQuoteDialogProps) {
  const [fullScreen, setFullScreen] = useState(props.fullScreen)
  const classes = useStyles({})
  const { user, preferences } = useUser()
  const { quote, estimator, save } = useQuote()
  const { options } = useClientOptions()
  const objectLabels = getObjectLabels({ options, invoice: quote.is_invoice })
  const { enqueueSnackbar } = useSnackbar()
  const { sendQuote } = useQuoteMutations()
  const [tab, setTab] = useState('email')
  const mobile = useIsMobile()
  const editorRef: React.Ref<EditorMethods> = useRef(null)
  const [bouncedEmailAddresses, setBouncedEmailAddresses] = useState<string[]>([])
  const { openDialog, dismissDialog } = useContext(DialogStackContext)
  const hasStripe = hasIntegrationInterface({ preferences, options, integration: 'stripe' })
  const hasStripeW1d = hasIntegrationInterface({ preferences, options, integration: 'stripeW1d' })
  const hasPayrix = hasIntegrationInterface({ preferences, options, integration: 'payrix' })
  const hasPayments = hasStripe || hasStripeW1d || hasPayrix
  const hasSettingsCommunications = getFeature({
    options,
    preferences,
    path: 'settingsCommunications.enabled'
  })

  const quoteLabel = view === WORK_ORDER_VIEW ? objectLabels.workOrder : objectLabels.quote

  const isSmallScreen = useBreakpoint((breakpoints) => breakpoints.down('sm'))
  const supportsTemplateSelection = view === 'quote' || view === 'presentation' || view === WORK_ORDER_VIEW

  const quoteType = quote.int_ext.value

  const baseEmailTemplates = getEmailTemplates({ options, userPreferences: preferences })
  const customEmailTemplateValues = getCustomEmailTemplates({ options, userPreferences: preferences })
  const allEmailTemplates = []
  if (customEmailTemplateValues) {
    Object.keys(customEmailTemplateValues).forEach((key) => allEmailTemplates.push(customEmailTemplateValues[key]))
  }
  allEmailTemplates.push(baseEmailTemplates['send-quote-client'])

  const enabledEmailTemplates = allEmailTemplates.filter((template) => template?.enabled !== false)
  const emailTemplates =
    enabledEmailTemplates.length > 0 ? enabledEmailTemplates : [baseEmailTemplates['send-quote-client']]

  const [selectedTemplateIndex, setSelectedTemplateIndex] = useState(() => {
    // only loop through for defaults if there is more than one
    if (emailTemplates.length > 1) {
      let i: number
      for (i = 0; i < emailTemplates.length; i++) {
        if (emailTemplates[i].defaultFor?.includes(quoteType)) {
          return i
        }
        if (i === emailTemplates.length - 1) return i
      }
    } else return 0
  })

  const [emails, setEmails] = useState(() => {
    const clientEmailTemplate =
      view === WORK_ORDER_VIEW
        ? baseEmailTemplates['send-work-order-staff']
        : quote.is_invoice
          ? baseEmailTemplates['send-invoice-client']
          : emailTemplates[selectedTemplateIndex]
    const staffEmailTemplateKey = quote.is_invoice
      ? hasSettingsCommunications
        ? 'send-invoice-staff'
        : 'invoice-sent-notification'
      : hasSettingsCommunications
        ? 'send-quote-staff'
        : 'quote-sent-notification'
    const staffEmailTemplate = baseEmailTemplates[staffEmailTemplateKey]

    const followUps = getFollowUps({ preferences, options })

    const quoteType = quote.int_ext.value
    const quoteFollowUps = followUps.order
      .map((key) => followUps.values[key])
      // only show followUps that would apply to this quote, hide all for work-order views
      .filter((followUp) => {
        if (view === WORK_ORDER_VIEW) return null
        else
          return (
            !!quote.is_invoice === !!followUp.isInvoice &&
            (quote.is_invoice ||
              followUp.quoteTypes?.includes(quoteType) ||
              quote.int_ext?.value === 'all' ||
              followUp.quoteTypes?.includes('__all__'))
          )
      })
      .map((followUp) => ({
        ...followUp,
        // a quote-level override wins; with no quote-level state, fall back to the template's sendEmail
        sendEmail:
          typeof quote.followUps?.[followUp.key]?.disabled !== 'undefined'
            ? !quote.followUps[followUp.key].disabled
            : !!followUp.sendEmail,
        sendText: quote.followUps?.[followUp.key]?.sendText ?? followUp.sendText
      }))

    return [
      {
        name: 'To Client',
        enabled: true,
        view,
        silentQuoteLink: view === WORK_ORDER_VIEW,
        signature: clientEmailTemplate.signature,
        values: {
          ...getDefaultEmail({
            user,
            quote,
            options,
            autofillTo: view === 'quote' || view === 'presentation',
            emailTemplate: clientEmailTemplate
          }),
          // only enable follow ups for 'To Client' emails
          view: view,
          followUps: quoteFollowUps,
          followUpsDisabled: quote.followUpsDisabled
        }
      },
      view === 'quote' && staffEmailTemplate?.enabled && options.options.features?.quotes?.emails?.toStaff
        ? {
            name: 'To Staff',
            view: 'quote',
            silentQuoteLink: true,
            signature: staffEmailTemplate.signature,
            enabled: true,
            values: {
              ...getDefaultEmail({
                user,
                quote,
                options,
                autofillTo: false,
                emailTemplate: staffEmailTemplate
              }),
              view: 'quote'
            }
          }
        : null
    ].filter((x) => !!x) as Email[]
  })

  const [selectedEmailIndex, setSelectedEmailIndex] = useState(0)

  const hasSMS = options.options?.features?.sms?.enabled ?? false
  const defaultTextSetting = options.options?.quotes?.sendQuoteByTextDefault ?? false
  const initialValues = {
    ...emails[selectedEmailIndex].values,
    sms: {
      ...emails[selectedEmailIndex].values.sms,
      enabled: !hasSMS ? false : defaultTextSetting ? true : emails[selectedEmailIndex].values.sms.enabled
    }
  }

  const renderedQuote = useMemo(() => {
    return (
      <div>
        <ThemeOverride customerFacing>
          <ScaledQuote
            quote={quote}
            estimator={estimator}
            view={view}
            classes={{ ...classes, paper: classes.quotePaper }}
          />
        </ThemeOverride>
      </div>
    )
  }, [])

  const handleBounceAlertMessage = (bouncedEmails: SentEmail[], sentEmails: SentEmail[]) => {
    const message = []
    // bounced
    message.push(
      <Typography key={'typography0'} variant="h3">
        Unable to Send to:{' '}
      </Typography>
    )
    message.push(<br key={'br0'} />)
    bouncedEmails.map((email) =>
      message.push(
        <Typography variant="body1" key={email.headers.to}>
          <strong>{email.headers.to}</strong>
        </Typography>
      )
    )
    message.push(<br key={'br1'} />)
    message.push(
      <Typography key={'typography1'} variant="body1" component="div">
        Please ensure you entered the above email address(es) correctly.
      </Typography>
    )
    // sent
    if (sentEmails.length) {
      message.push(<br key={'br2'} />)
      message.push(<Divider key={'div0'} />)
      message.push(<br key={'br3'} />)
      message.push(
        <Typography key={'typography2'} variant="body1">
          Successfully Sent to:
        </Typography>
      )
      message.push(<br key={'br4'} />)
      sentEmails.map((email) =>
        message.push(
          <Typography variant="body1" component="div" key={email.response.To}>
            {email.response.To}
          </Typography>
        )
      )
    }
    return message
  }

  return (
    <Dialog
      classes={{ paper: classes.paper, paperFullScreen: classes.paperFullScreen }}
      open={props.open}
      onClose={props.onCancel}
      forceFullScreen={mobile ? true : fullScreen}
      scroll="paper"
      fullWidth={true}
      maxWidth="lg"
    >
      <DialogTitle
        loading={props.loading}
        rightContent={
          // implementation="css" will prevent error on rightContent animation when hidden
          <Hidden smDown={true} implementation="css">
            <IconButton onClick={() => setFullScreen(!fullScreen)}>
              {fullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Hidden>
        }
      >
        Review &amp; Send
      </DialogTitle>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={ValidationSchema}
        validateOnBlur={false}
        validateOnChange={false}
        onSubmit={async (values) => {
          const selectedEmail = {
            ...emails[selectedEmailIndex],
            values
          }
          try {
            // update toggled followUps, only writing state for follow-ups that changed
            const updatedQuote: QuoteDocument = (values.followUps ?? []).reduce(
              (quote, followUp) => {
                const existingFollowUp = quote?.followUps?.[followUp.key]
                if (
                  existingFollowUp?.disabled !== !followUp.sendEmail ||
                  existingFollowUp?.sendText !== followUp.sendText
                ) {
                  return toggleFollowUp({
                    quote,
                    key: followUp.key,
                    enabled: followUp.sendEmail,
                    sendText: followUp.sendText
                  })
                }
                return quote
              },
              { ...quote, followUpsDisabled: values.followUpsDisabled }
            )
            // Save before sending, dont send if save fails
            const savedQuote = await save(updatedQuote)
            const validEmails = (values?.to ?? []).filter((email) => isEmailValid(email))
            if (!savedQuote) {
              console.log('Failed to save quote:', updatedQuote)
              console.log('res', savedQuote)
              Sentry.captureException(new Error('Failed to save quote before send'), { tags: { quoteId: quote._id } })
              enqueueSnackbar(
                `Unable to save ${objectLabels.quote.value.toLowerCase()} before sending. ${objectLabels.quote.value.toLowerCase()} was not sent.`,
                {
                  variant: 'error'
                }
              )
            } else if (
              selectedEmail?.name != 'To Staff' &&
              savedQuote?.status?.history?.sent?.timestamp &&
              savedQuote?.status?.history?.sent?.timestamp > Date.now() - 2 * 60000 &&
              50 > 75
            ) {
              enqueueSnackbar(
                "It looks like this quote was successfully sent very recently. To prevent spam, you'll be able to send again after 2 minutes.",
                { variant: 'warning' }
              )
            } else if (!validEmails.length && hasPayments && view !== WORK_ORDER_VIEW) {
              enqueueSnackbar("Contact email is invalid. Please update the contact's email address and try again.", {
                variant: 'error'
              })
            } else {
              const sendQuoteResponse = await sendQuote(quote._id, {
                emails: [
                  {
                    to: values.to,
                    from: values.from,
                    subject: values.subject,
                    body: values.body,
                    sms: values.sms?.enabled
                      ? {
                          to: [values.sms.to]
                        }
                      : null,
                    ...(values.reminderOptions?.enabled ? { reminderOptions: values.reminderOptions } : {}),
                    attachPdf: values.attachPdf,
                    silentQuoteLink: values.silentQuoteLink ?? selectedEmail.silentQuoteLink,
                    signature: selectedEmail.signature,
                    // Only set for work-order sends with translations enabled (selector gated in SendQuoteForm).
                    ...(values.lang ? { lang: values.lang } : {})
                  }
                ],
                view
              })

              track('quote_sent', {
                quoteId: quote._id,
                viewType: view,
                recipientCount: values.to?.length,
                smsEnabled: values.sms?.enabled ?? false,
                attachPdf: values.attachPdf ?? false,
                emailName: selectedEmail.name
              })

              const sentEmails = sendQuoteResponse.data.sendQuote?.emails ?? []
              const sentBouncedEmails = sentEmails.filter((email) => email.response.error === true)
              const sentSuccessEmails = sentEmails.filter((email) => email.response.ErrorCode === 0)
              if (!sentBouncedEmails.length) {
                onConfirm()
                enqueueSnackbar('Sent', { id: 'toast-quote-sent', variant: 'success' })
              } else {
                setBouncedEmailAddresses(sentBouncedEmails.map((email) => email.headers.to))
                openDialog(AlertDialog, {
                  icon: <ErrorIcon />,
                  title: `Unable to Send ${objectLabels.quote.value} to All Recipients`,
                  onConfirm: dismissDialog,
                  message: handleBounceAlertMessage(sentBouncedEmails, sentSuccessEmails)
                })
              }
            }
          } catch (error) {
            Sentry.captureException(error, { tags: { quoteId: quote._id } })
            const message = error?.graphQLErrors?.[0]?.message || 'Unable to send'
            enqueueSnackbar(message, { variant: 'error' })
          }
        }}
      >
        {({ values, handleSubmit, isSubmitting }) => {
          // put rendered form into a variable so we can take it in/out of the split view
          // when mobile
          const form = (
            <div className={classes.form}>
              <SendQuoteForm
                bouncedEmailAddresses={bouncedEmailAddresses.length ? bouncedEmailAddresses : null}
                id="send-quote-form"
                editorRef={editorRef}
                data-testid="send-quote-form"
                disabled={isSubmitting}
                emailName={emails[selectedEmailIndex].name}
                emailSelector={
                  emails.length > 1 && (
                    <DropdownSelect
                      label="Email"
                      margin="dense"
                      variant="single"
                      disabled={isSubmitting}
                      fullWidth
                      value={{
                        value: selectedEmailIndex,
                        label: emails[selectedEmailIndex].name
                      }}
                      options={emails.map((email, index) => ({
                        label: email.name,
                        value: index
                      }))}
                      onChange={(option: DropdownSelectOption) => {
                        const newIndex = option.value

                        // update current email if changing template
                        setEmails([
                          ...emails.slice(0, selectedEmailIndex),
                          {
                            ...emails[selectedEmailIndex],
                            values
                          },
                          ...emails.slice(selectedEmailIndex + 1)
                        ])

                        setSelectedEmailIndex(newIndex)
                      }}
                    />
                  )
                }
                templateSelector={
                  supportsTemplateSelection &&
                  !quote.is_invoice &&
                  emailTemplates.length > 1 &&
                  emails[selectedEmailIndex].name === 'To Client' && (
                    <DropdownSelect
                      label="Template"
                      margin="dense"
                      variant="single"
                      disabled={isSubmitting}
                      fullWidth
                      value={{
                        label: emailTemplates[selectedTemplateIndex]?.label,
                        value: selectedTemplateIndex
                      }}
                      options={emailTemplates.map((template, index) => ({
                        label: `${template.label}${
                          template.defaultFor?.includes(quoteType) || template.defaultFor?.includes('all')
                            ? ' - Default'
                            : ''
                        }`,
                        value: index
                      }))}
                      onChange={(option: DropdownSelectOption) => {
                        const newIndex = option.value
                        const newValues = getDefaultEmail({
                          user,
                          quote,
                          options,
                          autofillTo: true,
                          emailTemplate: emailTemplates[newIndex]
                        })

                        // update current email if changing template
                        // followUps are by quote type and so do not need to be recreated/updated on template change
                        setEmails([
                          ...emails.slice(0, selectedEmailIndex),
                          {
                            ...emails[selectedEmailIndex],
                            values: {
                              ...newValues,
                              followUps: values.followUps,
                              followUpsDisabled: values.followUpsDisabled
                            }
                          },
                          ...emails.slice(selectedEmailIndex + 1)
                        ])
                        editorRef.current.setHTML(newValues.body)
                        setSelectedTemplateIndex(newIndex)
                      }}
                    />
                  )
                }
              />
            </div>
          )

          return (
            <>
              <DialogContent classes={{ root: classes.dialogContent }}>
                <div className={classes.splitPaneWrapper}>
                  {isSmallScreen ? (
                    <>
                      <Tabs
                        value={tab}
                        variant="fullWidth"
                        className={classes.tabs}
                        onChange={(ev, newTab) => setTab(newTab)}
                      >
                        <Tab value="email" label="Email" />
                        <Tab value="quote" label={quoteLabel.value} />
                      </Tabs>
                      {tab === 'email' && form}
                      {tab === 'quote' && renderedQuote}
                    </>
                  ) : (
                    <SplitterLayout
                      customClassName={classes.splitterLayout}
                      percentage={true}
                      primaryMinSize={30}
                      secondaryMinSize={30}
                      pane2Style={{
                        overflow: 'scroll',
                        background: 'blue'
                      }}
                    >
                      {form}
                      {renderedQuote}
                    </SplitterLayout>
                  )}
                </div>
              </DialogContent>
              <DialogActions
                className={classes.dialogActions}
                alignLeftButtonWithTitle={false}
                leftButton={
                  <Button onClick={props.onCancel} variant="textNoPadding" disabled={isSubmitting}>
                    Cancel
                  </Button>
                }
              >
                <Button
                  icon={SendIcon}
                  variant={'contained'}
                  loading={isSubmitting}
                  disabled={isSubmitting}
                  onClick={() => handleSubmit()}
                >
                  {`Send ${quoteLabel.value}`}
                </Button>
              </DialogActions>
            </>
          )
        }}
      </Formik>
    </Dialog>
  )
}

// prevents re-render on every keystroke by using isEqual to compare props
export const ScaledQuote = React.memo(function ScaledQuote(
  props: QuoteProps & { classes: any; estimator: PresentationEstimator }
) {
  const { quote, estimator, classes, view } = props
  const [currentPage, setCurrentPage] = useState(null)
  const [navOpen, setNavOpen] = useState(false)
  const { options } = useClientOptions()
  const { wisetackPromo } = useQuote()
  const presentation = getQuotePresentation({
    options,
    quote,
    isWorkOrder: view === WORK_ORDER_VIEW
  })

  return (
    <Scaler fixedWidth={980}>
      <QuoteContextProvider quote={quote} estimator={estimator} contextValue={{ wisetackPromo, isPresentation: true }}>
        <PresentationNavContextProvider
          open={navOpen}
          currentPage={currentPage}
          view={view}
          popupMenu={true}
          onAcceptClick={() => {}}
          onPageClick={handleNavClick}
          onOpen={() => setNavOpen(true)}
          onClose={() => setNavOpen(false)}
        >
          <PresentationNavMobile classes={{ toolbar: classes.previewToolbar, appBar: classes.previewToolbarRoot }} />
          <div className={classes.presentationWrapper}>
            <Presentation presentation={presentation} classes={{}} page={currentPage} />
          </div>
        </PresentationNavContextProvider>
      </QuoteContextProvider>
    </Scaler>
  )

  function handleNavClick(ev: React.MouseEvent, value: string) {
    setCurrentPage(value)
    setNavOpen(false)
  }
}, isEqual)
